import React, { useEffect, useMemo, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import {
  approveInventoryCorrections,
  cancelInventoryCorrections,
  createInventoryCorrections,
  getCorrectionLineCosts,
  getDetailInventoryCorrections,
  getListProductCorrections,
  getLocations,
  InventoryCorrectionsPayload,
  rejectInventoryCorrections,
  updateInventoryCorrections,
} from '../../services/inventoryCorrections.service';
import { getDetailReasons } from '../../services/reason.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  CORRECTION_STATUS,
  ConfirmDialog,
  DocumentHeader,
  EmptyState,
  ErrorBanner,
  Fact,
  FieldMessage,
  IconButton,
  LinesCard,
  MenuItem,
  NextStep,
  NumberField,
  PageFrame,
  PhoneLabel,
  PhoneLine,
  PickerRow,
  ProductCell,
  ProductPicker,
  ReadValue,
  SaveBar,
  SelectField,
  Skeleton,
  StatusInfo,
  TotalsList,
  card,
  correctionStatusOf,
  defaultUnitId,
  fieldStyle,
  fullDateTime,
  labelStyle,
  money,
  quantity,
  signedMoney,
  tableStyle,
  td,
  th,
  unitsOfProduct,
  useLanguage,
} from '../../components/purchasing';
import { parseNumber, priceText } from '../Order/orderLineRules';
import {
  CorrectionLine,
  CorrectionReason,
  LineCost,
  changesOf,
  checkLine,
  costKey,
  costRequest,
  lineOfDetail,
  lineOfPick,
  linesPayload,
  reasonOf,
  signOf,
  withLineCost,
  withUnit,
} from './correctionLines';

/** The count screens read this shape from here. */
export interface ReasonResponse {
  id: string;
  reasonName: string;
}

const PICK_PAGE = 20;
/** The most lines POST /inventory-corrections/costs takes at once. */
const COST_BATCH = 200;
const COMMENT_MAX = 300;

type Mode = 'new' | 'edit' | 'read';

const personName = (user: any): string | null => {
  const name = [user?.firstName, user?.lastName].filter((part) => part && String(part).trim()).join(' ').trim();
  return name || null;
};

/** One page for a stock correction: written here, sent for approval, approved or rejected by a company admin. */
const CreateInventoryCorrections: React.FC = () => {
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const headerSite = localStorage.getItem('locationId') || '';
  const id: string = window.location.pathname.split('/').pop() || '';
  const isNew = id === 'new';
  const copyId = isNew ? new URLSearchParams(location.search).get('copy') || '' : '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [correction, setCorrection] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [lines, setLines] = useState<CorrectionLine[]>([]);
  const [saved, setSaved] = useState<CorrectionLine[]>([]);
  const [comment, setComment] = useState('');
  const [savedComment, setSavedComment] = useState('');
  const [siteId, setSiteId] = useState(headerSite);
  const [siteName, setSiteName] = useState('');
  const [reasons, setReasons] = useState<CorrectionReason[]>([]);
  const [flagged, setFlagged] = useState(false);
  const [notice, setNotice] = useState('');
  const [lineSearch, setLineSearch] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'approve' | 'reject' | 'cancel'>('');
  const [confirm, setConfirm] = useState<'' | 'reject' | 'cancel' | 'leave'>('');

  // Add products
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickTerm, setPickTerm] = useState('');
  const [picks, setPicks] = useState<any[]>([]);
  const [pickCount, setPickCount] = useState(0);
  const [pickPage, setPickPage] = useState(1);
  const [picksLoading, setPicksLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, string>>(() => new Map());
  const pickSeq = useRef(0);
  const costSeq = useRef(0);

  const statusId = Number(correction?.statusId);
  const canApprove = !!correction?.viewer?.canApprove;
  const isCreator = correction ? !!correction?.viewer?.isCreator || String(correction?.createdBy ?? '') === userId : false;
  const mode: Mode = isNew ? 'new' : statusId === CORRECTION_STATUS.Pending ? 'edit' : 'read';
  const editable = mode !== 'read';
  // The comment is the creator's note to the approver; everyone else reads it under Reason.
  const commentEditable = mode === 'new' || (mode === 'edit' && isCreator);
  const title = isNew ? t('New correction') : correction ? t('Correction #{number}', { number: correction.internalNumber }) : t('Correction');

  /* ---------------- Loading ---------------- */

  // Reasons of the lines, also when one is no longer in the active list.
  const reasonMap = useMemo(() => {
    const map = new Map(reasons.map((reason) => [reason.id, reason]));
    (correction?.inventoryCorrectionDetails ?? []).forEach((detail: any) => {
      const reason = reasonOf(detail);
      if (reason && !map.has(reason.id)) map.set(reason.id, reason);
    });
    return map;
  }, [reasons, correction]);

  const apply = (raw: any) => {
    const next = (raw?.inventoryCorrectionDetails ?? []).map(lineOfDetail);
    setCorrection(raw);
    setLines(next);
    setSaved(next);
    setComment(raw?.comment ?? '');
    setSavedComment(raw?.comment ?? '');
    setSiteId(String(raw?.locationId ?? headerSite));
    setSiteName(raw?.location?.name ?? '');
    setFlagged(false);
    setNotice('');
  };

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      if (isNew) {
        // A copy starts with the lines, comment and site of the correction it copies; nothing is saved yet.
        if (copyId) {
          const source = (await getDetailInventoryCorrections(copyId))?.data?.data;
          const next = (source?.inventoryCorrectionDetails ?? []).map(lineOfDetail);
          setLines(next);
          setComment(source?.comment ?? '');
          setSiteId(String(source?.locationId ?? headerSite));
          setSiteName(source?.location?.name ?? '');
          setCorrection({ copiedFrom: source?.internalNumber, inventoryCorrectionDetails: source?.inventoryCorrectionDetails ?? [] });
        } else setCorrection({});
        return;
      }
      apply((await getDetailInventoryCorrections(id))?.data?.data);
    } catch (error: any) {
      setFailed(true);
      showApiError(error, { language });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getDetailReasons(companyId, '1')
      .then((response: any) =>
        setReasons(
          (response?.data?.data ?? [])
            .filter((row: any) => row?.id && row?.isActive !== false)
            .map((row: any) => ({ id: String(row.id), name: String(row.reasonName ?? ''), impactType: row.impactType ?? null })),
        ),
      )
      .catch(() => undefined);
    const onToken = () => load();
    document.addEventListener('newToken', onToken);
    return () => document.removeEventListener('newToken', onToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A new correction names its site; a saved one brings the name with it.
  useEffect(() => {
    if (!isNew || siteName || !siteId) return;
    getLocations(userId, companyId, 0, 0, '')
      .then((response: any) => setSiteName((response?.data?.data ?? []).find((row: any) => String(row.id) === siteId)?.name ?? ''))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, siteId]);

  /* ---------------- Lines ---------------- */

  const checks = useMemo(() => new Map(lines.map((line) => [line.key, checkLine(line, reasonMap, flagged)])), [lines, reasonMap, flagged]);
  const changes = editable ? changesOf(lines, saved) : [];
  const commentChanged = mode !== 'read' && comment.trim() !== savedComment.trim();
  const dirty = changes.length > 0 || commentChanged;
  const attention = editable ? lines.filter((line) => checks.get(line.key)?.blocking).length : 0;

  const term = lineSearch.trim().toLowerCase();
  const shown = term ? lines.filter((line) => `${line.name} ${line.sku}`.toLowerCase().includes(term)) : lines;

  const patchLine = (key: string, next: (line: CorrectionLine) => CorrectionLine) => {
    setNotice('');
    setLines((prev) => prev.map((line) => (line.key === key ? next(line) : line)));
  };
  const removeLine = (key: string) => setLines((prev) => prev.filter((line) => line.key !== key));
  const discard = () => {
    setLines(saved);
    setComment(savedComment);
    setFlagged(false);
    setNotice('');
  };

  /* ---------------- Costs ---------------- */

  // The API prices each line from the site's stock layers, else the supplier; a change of product, unit, qty or reason asks again.
  const pendingCosts = editable && siteId ? lines.filter((line) => line.costFor !== costKey(line, siteId)) : [];
  const pendingKey = pendingCosts.map((line) => costKey(line, siteId)).join(',');

  const fetchCosts = async (asked: CorrectionLine[]): Promise<Map<string, LineCost>> => {
    const answers = new Map<string, LineCost>();
    for (let start = 0; start < asked.length; start += COST_BATCH) {
      const chunk = asked.slice(start, start + COST_BATCH);
      const response: any = await getCorrectionLineCosts({ locationId: siteId, lines: chunk.map(costRequest) });
      const rows: LineCost[] = response?.data?.data ?? [];
      chunk.forEach((line, index) => rows[index] && answers.set(costKey(line, siteId), rows[index]));
    }
    return answers;
  };
  // An answer applies only to a line still as it was asked about.
  const withCosts = (current: CorrectionLine[], answers: Map<string, LineCost>) =>
    current.map((line) => {
      const key = costKey(line, siteId);
      const known = answers.get(key);
      return known ? withLineCost(line, known, key) : line;
    });

  useDebounceEffect(() => {
    if (!pendingKey) return;
    const mine = ++costSeq.current;
    fetchCosts(pendingCosts)
      .then((answers) => setLines((prev) => withCosts(prev, answers)))
      .catch((error: any) => {
        if (mine === costSeq.current) showApiError(error, { language });
      });
  }, [pendingKey], 300);

  const valueOf = (line: CorrectionLine): number | null => {
    if (editable) return checks.get(line.key)?.value ?? null;
    const qty = parseNumber(line.qty) ?? 0;
    const cost = parseNumber(line.cost) ?? 0;
    return Math.round(signOf(reasonMap.get(line.reasonId)) * qty * cost * 100) / 100;
  };
  const values = lines.map(valueOf).filter((value): value is number => value !== null);
  const adds = values.filter((value) => value > 0).reduce((sum, value) => sum + value, 0);
  const removes = values.filter((value) => value < 0).reduce((sum, value) => sum + value, 0);
  const totalRows = [
    ...(adds !== 0 && removes !== 0
      ? [
          { label: t('Adds'), value: signedMoney(adds) },
          { label: t('Removes'), value: money(removes) },
        ]
      : []),
    { label: t('Stock impact'), value: signedMoney(adds + removes), strong: true },
  ];

  /* ---------------- Actions ---------------- */

  const backToList = () => webNavigate(`/${preLanguage}/corrections`);
  const onBack = () => (dirty || (isNew && lines.length > 0) ? setConfirm('leave') : backToList());

  const flagAndFocus = () => {
    setFlagged(true);
    window.setTimeout(() => {
      const first = document.querySelector<HTMLElement>('[data-lines] [aria-invalid="true"]');
      first?.scrollIntoView({ block: 'center' });
      first?.focus();
    }, 0);
  };

  const payload = (current: CorrectionLine[]): InventoryCorrectionsPayload => ({
    companyId,
    locationId: siteId,
    statusId: CORRECTION_STATUS.Pending,
    reasonId: null,
    comment: comment.trim(),
    inventoryCorrectionDetails: linesPayload(current),
  });

  const save = async () => {
    if (busy) return;
    if (lines.length === 0) {
      setNotice(t('Add at least one product.'));
      return;
    }
    if (lines.some((line) => checkLine(line, reasonMap, true).blocking)) {
      flagAndFocus();
      return;
    }
    setBusy('save');
    try {
      // Costs still being worked out come first, so what is sent is what the page shows.
      let current = lines;
      if (pendingCosts.length) {
        current = withCosts(lines, await fetchCosts(pendingCosts));
        setLines(current);
        if (current.some((line) => checkLine(line, reasonMap, true).blocking)) {
          flagAndFocus();
          return;
        }
      }
      if (isNew) {
        const response: any = await createInventoryCorrections(payload(current));
        const created = response?.data?.data;
        showToast({ message: t('Correction #{number} sent for approval', { number: created?.internalNumber ?? '' }), type: 'success', duration: 5000 });
        if (created?.id) webNavigate(`/${preLanguage}/corrections/${created.id}`);
        else backToList();
      } else {
        await updateInventoryCorrections(id, payload(current));
        showToast({ message: t('Changes saved'), type: 'success', duration: 5000 });
        await load();
      }
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const decide = async (action: 'approve' | 'reject' | 'cancel') => {
    setBusy(action);
    try {
      if (action === 'approve') await approveInventoryCorrections(id);
      else if (action === 'reject') await rejectInventoryCorrections(id);
      else await cancelInventoryCorrections(id);
      setConfirm('');
      showToast({
        message: action === 'approve' ? t('Correction approved. Stock is updated at {site}.', { site: siteName }) : action === 'reject' ? t('Correction rejected') : t('Correction cancelled'),
        type: 'success',
        duration: 6000,
      });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const copyToNew = () => webNavigate(`/${preLanguage}/corrections/new?copy=${isNew ? copyId : id}`);

  /* ---------------- Add products ---------------- */

  useDebounceEffect(() => setPickTerm(pickSearch.trim()), [pickSearch], 300);

  const loadPicks = async (page: number, append: boolean) => {
    const mine = ++pickSeq.current;
    setPicksLoading(true);
    try {
      const response: any = await getListProductCorrections(page, PICK_PAGE, pickTerm, isNew ? '' : id, siteId);
      if (mine !== pickSeq.current) return;
      const rows: any[] = response?.data?.productWithCompany ?? [];
      setPicks((prev) => {
        if (!append) return rows;
        const known = new Set(prev.map((row) => String(row.id)));
        return [...prev, ...rows.filter((row) => !known.has(String(row.id)))];
      });
      setPickCount(Number(response?.data?.count) || 0);
      setPickPage(page);
    } catch (error: any) {
      if (mine === pickSeq.current) showApiError(error, { language });
    } finally {
      if (mine === pickSeq.current) setPicksLoading(false);
    }
  };

  useEffect(() => {
    if (pickerOpen) loadPicks(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickTerm]);

  const openPicker = () => {
    setPickSearch('');
    setPickTerm('');
    setSelected(new Map());
    setPickerOpen(true);
  };

  const onCorrection = new Set(lines.map((line) => line.productId));
  const pickerRows: PickerRow[] = picks.map((pick) => {
    const productId = String(pick.id);
    const units = unitsOfProduct(pick);
    return {
      id: productId,
      name: pick?.productName ?? '',
      sku: pick?.sku ?? null,
      group: pick?.productGroup?.groupName ?? null,
      units: units.map((unit) => ({ id: unit.id, name: unit.name, taken: onCorrection.has(productId) })),
      selected: selected.has(productId),
      unitId: selected.get(productId) || lineOfPick(pick).productUOMId || defaultUnitId(units),
    };
  });

  const togglePick = (row: PickerRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else if (row.unitId) next.set(row.id, row.unitId);
      return next;
    });
  const pickUnit = (row: PickerRow, unitId: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(row.id, unitId);
      return next;
    });

  const addPicked = () => {
    const known = new Set(lines.map((line) => line.productId));
    const added: CorrectionLine[] = [];
    selected.forEach((unitId, productId) => {
      const pick = picks.find((row) => String(row.id) === productId);
      if (!pick || !unitId || known.has(productId)) return;
      known.add(productId);
      added.push(lineOfPick(pick, unitId));
    });
    setLines((prev) => [...prev, ...added]);
    setLineSearch('');
    setPickerOpen(false);
  };

  /* ---------------- Header ---------------- */

  const lineReasons = [...new Set(lines.map((line) => reasonMap.get(line.reasonId)?.name).filter(Boolean))] as string[];
  const approver = personName(correction?.approvedByUser);
  let approval: Pick<Fact, 'value' | 'pending' | 'lines'>;
  if (mode === 'new') approval = { pending: t('Not sent for approval yet') };
  else if (statusId === CORRECTION_STATUS.Pending) approval = { pending: t('Waiting'), lines: [t('Any company admin can approve')] };
  else if (statusId === CORRECTION_STATUS.Rejected)
    approval = { value: t('Rejected'), lines: [[fullDateTime(correction?.approvalDate ?? correction?.updatedAt), approver].filter(Boolean).join(' · ')] };
  else if (statusId === CORRECTION_STATUS.Cancelled) approval = { value: t('Cancelled'), lines: [fullDateTime(correction?.updatedAt)] };
  else if (correction?.approvalDate) approval = { value: fullDateTime(correction.approvalDate), lines: [approver].filter(Boolean) as string[] };
  else approval = { pending: '—' };

  const facts: Fact[] = [
    { key: 'site', label: t('Site'), value: siteName, pending: '—' },
    {
      key: 'reason',
      label: t('Reason'),
      value: lineReasons.join(', '),
      pending: '—',
      // The comment reads under the reason once the correction is saved; while editing it has its own field.
      lines: !commentEditable && correction?.comment ? [correction.comment] : [],
    },
    {
      key: 'created',
      label: t('Created'),
      value: mode === 'new' ? undefined : fullDateTime(correction?.createdAt),
      pending: t('Not saved yet'),
      lines: mode === 'new' ? [] : ([personName(correction?.createdByUser)].filter(Boolean) as string[]),
      numeric: true,
    },
    { key: 'approval', label: t('Approval'), numeric: true, stackLines: true, ...approval },
  ];

  const status: StatusInfo = mode === 'new' ? { label: 'New', family: 'draft' } : correctionStatusOf(statusId);
  const copyButton = (
    <Button phone={phone} fullWidth={phone} icon="copy" onClick={copyToNew}>
      {t('Copy to new correction')}
    </Button>
  );
  let nextStep: NextStep | null = null;
  if (statusId === CORRECTION_STATUS.Pending && mode === 'edit') {
    if (canApprove) {
      const approveButton = (
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={!!busy || dirty} aria-busy={busy === 'approve' || undefined} onClick={() => decide('approve')}>
          {busy === 'approve' ? t('Approving…') : t('Approve correction')}
        </Button>
      );
      const rejectButton = (
        <Button variant="danger" phone={phone} fullWidth={phone} disabled={!!busy || dirty} onClick={() => setConfirm('reject')}>
          {t('Reject correction')}
        </Button>
      );
      nextStep = {
        tone: 'waiting',
        text: dirty ? t('Save your changes, then approve.') : t('Waiting for your approval. Approving changes the stock at {site}.', { site: siteName }),
        actions: phone ? (
          <>
            {approveButton}
            {rejectButton}
          </>
        ) : (
          <>
            {rejectButton}
            {approveButton}
          </>
        ),
      };
    } else nextStep = { tone: 'waiting', text: t('Waiting for a company admin to approve.') };
  } else if (statusId === CORRECTION_STATUS.Rejected) {
    nextStep = { tone: 'stopped', text: approver ? t('Rejected by {name}', { name: approver }) : t('This correction was rejected. No stock changed.'), actions: copyButton };
  } else if (statusId === CORRECTION_STATUS.Cancelled) {
    nextStep = { tone: 'stopped', text: t('This correction was cancelled. No stock changed.'), actions: copyButton };
  }

  const moreItems: MenuItem[] = [
    ...(editable && phone && dirty && mode === 'edit' ? [{ key: 'discard', label: t('Discard changes'), icon: 'undo' as const, onSelect: discard }] : []),
    ...(mode === 'read' && statusId !== CORRECTION_STATUS.Rejected && statusId !== CORRECTION_STATUS.Cancelled
      ? [{ key: 'copy', label: t('Copy to new correction'), icon: 'copy' as const, onSelect: copyToNew }]
      : []),
    ...(mode === 'edit' && isCreator ? [{ key: 'cancel', label: t('Cancel correction'), icon: 'close' as const, danger: true, onSelect: () => setConfirm('cancel') }] : []),
  ];

  /* ---------------- Lines card ---------------- */

  const reasonSelect = (line: CorrectionLine, selectId: string, check: any) => {
    const removing = reasons.filter((reason) => signOf(reason) < 0);
    const adding = reasons.filter((reason) => signOf(reason) > 0);
    const current = reasonMap.get(line.reasonId);
    return (
      <SelectField
        id={selectId}
        phone={phone}
        invalid={!!check?.reasonError}
        aria-invalid={!!check?.reasonError || undefined}
        aria-describedby={check?.reasonError ? `${selectId}-msg` : undefined}
        value={line.reasonId}
        onChange={(event) => patchLine(line.key, (prev) => ({ ...prev, reasonId: event.target.value }))}
      >
        <option value="">{t('Choose a reason')}</option>
        {current && !reasons.some((reason) => reason.id === current.id) && <option value={current.id}>{current.name}</option>}
        {removing.length > 0 && (
          <optgroup label={t('Removes stock')}>
            {removing.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.name}
              </option>
            ))}
          </optgroup>
        )}
        {adding.length > 0 && (
          <optgroup label={t('Adds stock')}>
            {adding.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.name}
              </option>
            ))}
          </optgroup>
        )}
      </SelectField>
    );
  };

  const unitSelect = (line: CorrectionLine, selectId: string) => (
    <SelectField id={selectId} phone={phone} value={line.productUOMId} onChange={(event) => patchLine(line.key, (prev) => withUnit(prev, event.target.value))} wrapperStyle={phone ? { flex: 'none', width: '116px' } : undefined}>
      {!line.productUOMId && <option value="">{t('Choose a unit')}</option>}
      {line.units.map((unit) => (
        <option key={unit.id} value={unit.id}>
          {unit.name}
        </option>
      ))}
    </SelectField>
  );

  const message = (id: string, text: string) =>
    text ? (
      <FieldMessage id={`${id}-msg`} tone="error">
        {t(text)}
      </FieldMessage>
    ) : null;

  const qtyInput = (line: CorrectionLine, inputId: string, check: any) => (
    <>
      <NumberField
        id={inputId}
        column="qty"
        phone={phone}
        srLabel={phone ? undefined : t('Qty for {product}', { product: line.name })}
        value={line.qty}
        invalid={!!check?.qtyError}
        describedBy={check?.qtyError ? `${inputId}-msg` : undefined}
        onChange={(value) => patchLine(line.key, (prev) => ({ ...prev, qty: value }))}
      />
      {message(inputId, check?.qtyError)}
    </>
  );

  const costInput = (line: CorrectionLine, inputId: string, check: any) => (
    <>
      <NumberField
        id={inputId}
        column="cost"
        phone={phone}
        prefix="€"
        srLabel={phone ? undefined : t('Cost for {product}', { product: line.name })}
        value={line.cost}
        invalid={!!check?.costError}
        describedBy={check?.costError ? `${inputId}-msg` : undefined}
        onChange={(value) => patchLine(line.key, (prev) => ({ ...prev, cost: value }))}
        onBlur={() => {
          const n = parseNumber(line.cost);
          if (n !== null && Number.isFinite(n)) patchLine(line.key, (prev) => ({ ...prev, cost: priceText(n) }));
        }}
      />
      {message(inputId, check?.costError)}
    </>
  );

  const valueText = (value: number | null) => (value === null ? '—' : signedMoney(value));
  // A cost the API gives reads as text; only one to enter is a field.
  const costValue = (line: CorrectionLine, phoneView = false) => (
    <ReadValue phone={phoneView} muted={line.cost.trim() === ''}>
      {money(parseNumber(line.cost))}
    </ReadValue>
  );
  const unitNameOf = (line: CorrectionLine) => line.units.find((unit) => unit.id === line.productUOMId)?.name ?? '';
  const signedQty = (line: CorrectionLine) => `${signOf(reasonMap.get(line.reasonId)) < 0 ? '−' : '+'}${quantity(parseNumber(line.qty) ?? 0)}`;

  const desktopTable = () => (
    <div data-lines="" style={{ overflowX: 'auto' }}>
      <table style={tableStyle(editable ? 880 : 760)}>
        <thead>
          <tr>
            <th scope="col" style={th('left', { first: true })}>
              {t('Product')}
            </th>
            <th scope="col" style={th('left', { width: editable ? 172 : 150 })}>
              {t('Reason')}
            </th>
            <th scope="col" style={th('left', { width: editable ? 116 : 96 })}>
              {t('Unit')}
            </th>
            <th scope="col" style={th('right', { width: editable ? 96 : 80 })}>
              {t('Qty')}
            </th>
            <th scope="col" style={th('right', { width: 108 })}>
              {t('Cost')}
            </th>
            <th scope="col" style={th('right', editable ? { width: 96, padRight: 0 } : { width: 110, last: true })}>
              {t('Value')}
            </th>
            {editable && (
              <th scope="col" style={th('left', { width: 44, padLeft: 2, padRight: 10 })}>
                <span className="pu-sr">{t('Remove')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {shown.map((line) => {
            const check = checks.get(line.key);
            const error = editable && !!check?.blocking;
            const value = valueOf(line);
            return (
              <tr key={line.key} className="pu-row">
                <td style={td({ first: true, error })}>
                  <ProductCell name={line.name} sku={line.sku} context={line.group} />
                </td>
                <td style={td({ error })}>
                  {editable ? (
                    <>
                      <label className="pu-sr" htmlFor={`reason-${line.key}`}>
                        {t('Reason for {product}', { product: line.name })}
                      </label>
                      {reasonSelect(line, `reason-${line.key}`, check)}
                      {message(`reason-${line.key}`, check?.reasonError ?? '')}
                    </>
                  ) : (
                    <ReadValue>{reasonMap.get(line.reasonId)?.name ?? '—'}</ReadValue>
                  )}
                </td>
                <td style={td({ error })}>
                  {editable ? (
                    <>
                      <label className="pu-sr" htmlFor={`unit-${line.key}`}>
                        {t('Unit for {product}', { product: line.name })}
                      </label>
                      {unitSelect(line, `unit-${line.key}`)}
                    </>
                  ) : (
                    <ReadValue>{unitNameOf(line) || '—'}</ReadValue>
                  )}
                </td>
                <td style={td({ align: 'right', error })}>{editable ? qtyInput(line, `qty-${line.key}`, check) : <ReadValue>{signedQty(line)}</ReadValue>}</td>
                <td style={td({ align: 'right', error })}>{editable && line.costEditable ? costInput(line, `cost-${line.key}`, check) : costValue(line)}</td>
                <td style={td(editable ? { align: 'right', padRight: 0, error } : { align: 'right', last: true })}>
                  <ReadValue strong={value !== null} muted={value === null}>
                    {valueText(value)}
                  </ReadValue>
                </td>
                {editable && (
                  <td style={td({ align: 'right', padLeft: 2, padRight: 10, padTop: 16, error })}>
                    <IconButton icon="close" remove label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const phoneLines = () => (
    <div data-lines="">
      {shown.map((line) => {
        const check = checks.get(line.key);
        const nameId = `line-${line.key}`;
        const value = valueOf(line);
        if (!editable) {
          return (
            <PhoneLine key={line.key} labelledBy={nameId}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <ProductCell phone nameId={nameId} name={line.name} sku={line.sku} context={reasonMap.get(line.reasonId)?.name ?? line.group} />
                <p style={{ flex: 'none', fontSize: '15px', lineHeight: '22px', color: '#222525' }}>
                  <span className="pu-sr">{t('Unit')}: </span>
                  {unitNameOf(line)}
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px', textAlign: 'right' }}>
                <div>
                  <PhoneLabel align="right">{t('Qty')}</PhoneLabel>
                  <ReadValue phone>{signedQty(line)}</ReadValue>
                </div>
                <div>
                  <PhoneLabel align="right">{t('Cost')}</PhoneLabel>
                  {costValue(line, true)}
                </div>
                <div>
                  <PhoneLabel align="right">{t('Value')}</PhoneLabel>
                  <ReadValue phone strong>
                    {valueText(value)}
                  </ReadValue>
                </div>
              </div>
            </PhoneLine>
          );
        }
        return (
          <PhoneLine key={line.key} labelledBy={nameId} error={!!check?.blocking}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <ProductCell phone nameId={nameId} name={line.name} sku={line.sku} context={line.group} />
              <label className="pu-sr" htmlFor={`m-unit-${line.key}`}>
                {t('Unit for {product}', { product: line.name })}
              </label>
              {unitSelect(line, `m-unit-${line.key}`)}
              <IconButton icon="close" remove phone label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} style={{ marginRight: '-16px' }} />
            </div>
            <div style={{ marginTop: '10px' }}>
              <PhoneLabel htmlFor={`m-reason-${line.key}`}>{t('Reason')}</PhoneLabel>
              {reasonSelect(line, `m-reason-${line.key}`, check)}
              {message(`m-reason-${line.key}`, check?.reasonError ?? '')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <PhoneLabel htmlFor={`m-qty-${line.key}`}>{t('Qty')}</PhoneLabel>
                {qtyInput(line, `m-qty-${line.key}`, check)}
              </div>
              {line.costEditable ? (
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel htmlFor={`m-cost-${line.key}`}>{t('Cost')}</PhoneLabel>
                  {costInput(line, `m-cost-${line.key}`, check)}
                </div>
              ) : (
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel>{t('Cost')}</PhoneLabel>
                  {costValue(line, true)}
                </div>
              )}
              <div style={{ minWidth: 0, textAlign: 'right' }}>
                <PhoneLabel align="right">{t('Value')}</PhoneLabel>
                <ReadValue phone strong={value !== null} muted={value === null}>
                  {valueText(value)}
                </ReadValue>
              </div>
            </div>
          </PhoneLine>
        );
      })}
    </div>
  );

  const linesBody = () => {
    if (loading && !correction) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: phone ? '16px' : '20px' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      );
    }
    if (lines.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          {editable ? (
            <EmptyState
              phone={phone}
              title={t('No products on this correction yet')}
              desc={t('Use Add products to choose what to correct at {site}.', { site: siteName || t('this site') })}
              action={
                <Button variant="primary" phone={phone} icon="plus" onClick={openPicker}>
                  {t('Add products')}
                </Button>
              }
            />
          ) : (
            <EmptyState phone={phone} title={t('No products on this correction')} />
          )}
        </div>
      );
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          <EmptyState
            icon="search"
            phone={phone}
            title={t('No products on this correction match "{search}". Check the spelling or search by SKU.', { search: lineSearch.trim() })}
            action={
              <Button variant="text" phone={phone} onClick={() => setLineSearch('')}>
                {t('Clear search')}
              </Button>
            }
          />
        </div>
      );
    }
    return phone ? phoneLines() : desktopTable();
  };

  /* ---------------- Comment and save bar ---------------- */

  const commentCard = commentEditable && (
    <section aria-labelledby="pu-comment-title" style={{ ...card, display: 'flex', flexDirection: 'column', gap: '8px', padding: phone ? '16px' : '20px' }}>
      <label id="pu-comment-title" htmlFor="pu-comment" style={labelStyle}>
        {t('Comment for the approver')}
      </label>
      <textarea
        id="pu-comment"
        className="pu-input"
        value={comment}
        maxLength={COMMENT_MAX}
        rows={2}
        placeholder={t('What happened? For example: crate dropped during delivery.')}
        onChange={(event) => setComment(event.target.value)}
        style={{ ...fieldStyle(phone), height: 'auto', minHeight: phone ? '72px' : '64px', padding: '10px 12px', resize: 'vertical' }}
      />
      <p style={{ fontSize: '12px', lineHeight: '16px', color: '#4A5353', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {comment.length}/{COMMENT_MAX}
      </p>
    </section>
  );

  const changeText = (change: { kind: string; key: string }) => {
    const line = (change.kind === 'removed' ? saved : lines).find((other) => other.key === change.key);
    const name = line?.name ?? '';
    return change.kind === 'added' ? t('Added {name}', { name }) : change.kind === 'removed' ? t('Removed {name}', { name }) : t('Edited {name}', { name });
  };
  const changeCount = changes.length + (commentChanged ? 1 : 0);
  const attentionText = attention ? (attention === 1 ? t('1 line needs attention') : t('{count} lines need attention', { count: attention })) : null;
  const summary =
    mode === 'new'
      ? {
          lead: lines.length === 1 ? t('1 product') : t('{count} products', { count: lines.length }),
          detail: lines.length ? [t('Stock impact {amount}', { amount: signedMoney(adds + removes) })] : [],
          attention: notice || attentionText,
          quiet: lines.length === 0,
        }
      : {
          lead: changeCount === 1 ? t('1 unsaved change') : t('{count} unsaved changes', { count: changeCount }),
          detail: changes.length ? [changes.length > 1 ? t('{first}, +{count} more', { first: changeText(changes[0]), count: changes.length - 1 }) : changeText(changes[0])] : commentChanged ? [t('Edited the comment')] : [],
          attention: notice || attentionText,
        };
  // A waiting correction shows the bar only once something changed; a new one always has it.
  const saveBar = editable && (mode === 'new' || dirty) && (
    <SaveBar
      phone={phone}
      label={mode === 'new' ? t('New correction') : t('Unsaved changes')}
      summary={summary}
      secondary={
        mode === 'edit' && !phone ? (
          <Button variant="text" disabled={!!busy} onClick={discard}>
            {t('Discard changes')}
          </Button>
        ) : undefined
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={!!busy || (mode === 'new' && lines.length === 0)} aria-busy={busy === 'save' || undefined} onClick={save}>
          {busy === 'save' ? (mode === 'new' ? t('Sending…') : t('Saving…')) : mode === 'new' ? t('Send for approval') : t('Save changes')}
        </Button>
      }
    />
  );

  const number = `#${correction?.internalNumber ?? ''}`;
  return (
    <>
      <PageFrame phone={phone} withPhoneBar={!!saveBar}>
        {failed && !correction ? (
          <ErrorBanner message={t('The correction could not be loaded.')} retryLabel={t('Try again')} onRetry={load} />
        ) : (
          <>
            {correction ? (
              <DocumentHeader phone={phone} t={t} backLabel={t('Back to corrections')} onBack={onBack} title={title} status={status} nextStep={nextStep} facts={facts} moreItems={moreItems} />
            ) : (
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
                <Skeleton width="120px" />
                <Skeleton width="220px" height={28} />
                <Skeleton height={96} />
              </section>
            )}
            {commentCard}
            <LinesCard
              phone={phone}
              titleId="pu-lines-title"
              title={t('Products')}
              count={correction ? lines.length : undefined}
              search={lines.length > 0 ? { label: t('Find a product in this correction'), placeholder: t('Find a product in this correction'), value: lineSearch, onChange: setLineSearch } : undefined}
              actions={
                editable && lines.length > 0 ? (
                  <Button phone={phone} icon="plus" onClick={openPicker}>
                    {t('Add products')}
                  </Button>
                ) : undefined
              }
              footer={lines.length > 0 && <TotalsList phone={phone} rows={totalRows} />}
            >
              {linesBody()}
            </LinesCard>
            {saveBar}
          </>
        )}
      </PageFrame>
      {editable && (
        <ProductPicker
          open={pickerOpen}
          phone={phone}
          t={t}
          title={t('Add products to {correction}', { correction: title })}
          search={pickSearch}
          onSearch={setPickSearch}
          groups={[]}
          groupId=""
          onGroup={() => undefined}
          rows={pickerRows}
          loading={picksLoading}
          hasMore={picks.length < pickCount}
          onMore={() => loadPicks(pickPage + 1, true)}
          onToggle={togglePick}
          onUnit={pickUnit}
          selectedCount={selected.size}
          onCancel={() => setPickerOpen(false)}
          onAdd={addPicked}
          takenLabel={t('On this correction')}
          unitLabel={(product) => t('Unit for {product}', { product })}
          searchPlaceholder={t('Name or SKU')}
        />
      )}
      <ConfirmDialog
        open={confirm === 'reject'}
        phone={phone}
        danger
        busy={busy === 'reject'}
        title={t('Reject correction {number}?', { number })}
        body={t('The correction is marked rejected and the stock at {site} does not change. This cannot be undone.', { site: siteName })}
        confirmLabel={t('Reject correction')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        onConfirm={() => decide('reject')}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        phone={phone}
        danger
        busy={busy === 'cancel'}
        title={t('Cancel correction {number}?', { number })}
        body={t('The correction is withdrawn before approval and the stock does not change. This cannot be undone.')}
        confirmLabel={t('Cancel correction')}
        cancelLabel={t('Keep correction')}
        closeLabel={t('Close')}
        onConfirm={() => decide('cancel')}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'leave'}
        phone={phone}
        danger
        title={t('Leave without saving?')}
        body={mode === 'new' ? t('This correction is not saved yet.') : t('Your changes to this correction are not saved.')}
        confirmLabel={t('Leave without saving')}
        cancelLabel={t('Keep editing')}
        closeLabel={t('Close')}
        onConfirm={() => {
          setConfirm('');
          backToList();
        }}
        onCancel={() => setConfirm('')}
      />
    </>
  );
};

export default CreateInventoryCorrections;
